import type {
  BuiltForm,
  FieldType,
  FormField,
} from '../models/built-form';
import { allFormFields, createDefaultPage } from './form-pages';

export type {
  BuiltForm,
  ChatMessage,
  FieldType,
  FieldValidation,
  FormField,
  HeadingLevel,
  SpacerSize,
} from '../models/built-form';

export interface GenerateResult {
  form: BuiltForm;
  assistantMessage: string;
}

let fieldCounter = 0;

function field(
  type: FieldType,
  label: string,
  extras: Partial<Omit<FormField, 'id' | 'type' | 'label'>> = {},
): FormField {
  fieldCounter += 1;
  return {
    id: `field-${fieldCounter}`,
    type,
    label,
    ...extras,
  };
}

function asSinglePageForm(
  form: Omit<BuiltForm, 'pages'> & {
    fields: FormField[];
    submitLabel?: string;
  },
): BuiltForm {
  const { fields, submitLabel, ...rest } = form;
  return {
    ...rest,
    pages: [
      createDefaultPage({
        fields,
        cta: { primaryLabel: submitLabel?.trim() || 'Submit' },
      }),
    ],
  };
}

const TEMPLATES: Array<{
  keys: string[];
  form: () => BuiltForm;
}> = [
  {
    keys: ['job', 'application', 'career', 'hiring', 'resume'],
    form: () =>
      asSinglePageForm({
        title: 'Job Application',
        description:
          'Apply for an open role. We will review your details and get back to you.',
        fields: [
          field('text', 'Full name', {
            required: true,
            placeholder: 'Alex Thompson',
          }),
          field('email', 'Email', {
            required: true,
            placeholder: 'alex@example.com',
          }),
          field('phone', 'Phone', { placeholder: '+1 555 000 0000' }),
          field('select', 'Role applied for', {
            required: true,
            options: ['Engineering', 'Design', 'Sales', 'Operations'],
          }),
          field('textarea', 'Why are you a good fit?', {
            required: true,
            placeholder: 'Share a short summary of your experience…',
          }),
          field('text', 'LinkedIn / Portfolio URL', { placeholder: 'https://…' }),
        ],
      }),
  },
  {
    keys: ['feedback', 'survey', 'customer', 'nps', 'review'],
    form: () =>
      asSinglePageForm({
        title: 'Customer Feedback Survey',
        description: 'Help us improve by sharing your recent experience.',
        fields: [
          field('text', 'Name', { placeholder: 'Optional' }),
          field('email', 'Email', { placeholder: 'you@company.com' }),
          field('select', 'Overall satisfaction', {
            required: true,
            options: ['Very satisfied', 'Satisfied', 'Neutral', 'Dissatisfied'],
          }),
          field('select', 'Would you recommend us?', {
            required: true,
            options: ['Definitely', 'Probably', 'Not sure', 'No'],
          }),
          field('textarea', 'What could we improve?', {
            placeholder: 'Tell us what stood out…',
          }),
          field('checkbox', 'Okay to contact me about this feedback'),
        ],
      }),
  },
  {
    keys: ['onboarding', 'new hire', 'employee', 'questionnaire', 'welcome'],
    form: () =>
      asSinglePageForm({
        title: 'User Onboarding Questionnaire',
        description:
          'Collect the essentials so your team can get productive on day one.',
        fields: [
          field('text', 'Full name', { required: true }),
          field('email', 'Work email', { required: true }),
          field('select', 'Department', {
            required: true,
            options: ['Engineering', 'Product', 'People', 'Finance', 'Other'],
          }),
          field('date', 'Start date', { required: true }),
          field('select', 'Equipment needed', {
            options: ['Laptop', 'Monitor', 'Headset', 'None'],
          }),
          field('textarea', 'Anything else we should know?', {
            placeholder: 'Dietary needs, accessibility, preferences…',
          }),
        ],
      }),
  },
  {
    keys: ['event', 'registration', 'rsvp', 'ticket', 'webinar'],
    form: () =>
      asSinglePageForm({
        title: 'Event Registration',
        description: 'Reserve your spot for the upcoming event.',
        fields: [
          field('text', 'Full name', { required: true }),
          field('email', 'Email', { required: true }),
          field('phone', 'Phone'),
          field('select', 'Ticket type', {
            required: true,
            options: ['General admission', 'VIP', 'Virtual'],
          }),
          field('select', 'Number of guests', {
            options: ['Just me', '1 guest', '2 guests', '3+ guests'],
          }),
          field('textarea', 'Dietary restrictions / notes'),
          field('checkbox', 'I agree to the event terms', { required: true }),
        ],
      }),
  },
  {
    keys: ['contact', 'name, email', 'message', 'inquiry', 'support'],
    form: () =>
      asSinglePageForm({
        title: 'Contact Form',
        description: 'Send us a message and we will reply soon.',
        fields: [
          field('text', 'Name', { required: true, placeholder: 'Your name' }),
          field('email', 'Email', {
            required: true,
            placeholder: 'you@example.com',
          }),
          field('phone', 'Phone', { placeholder: 'Optional' }),
          field('textarea', 'Message', {
            required: true,
            placeholder: 'How can we help?',
          }),
        ],
      }),
  },
];

function defaultForm(prompt: string): BuiltForm {
  const titleGuess =
    prompt.length > 48
      ? `${prompt.slice(0, 45).trim()}…`
      : prompt.trim() || 'Untitled Form';

  return asSinglePageForm({
    title:
      titleGuess
        .replace(/^(create|build|make|design|generate)\s+(a|an)\s+/i, '')
        .replace(/\b\w/g, (c) => c.toUpperCase()) || 'Custom Form',
    description: 'Generated from your prompt. Refine it by chatting with AI.',
    fields: [
      field('text', 'Full name', { required: true }),
      field('email', 'Email', { required: true }),
      field('textarea', 'Details', {
        placeholder: 'Add any additional information…',
      }),
    ],
  });
}

function matchTemplate(prompt: string): BuiltForm {
  const lower = prompt.toLowerCase();
  for (const template of TEMPLATES) {
    if (template.keys.some((key) => lower.includes(key))) {
      return template.form();
    }
  }
  return defaultForm(prompt);
}

function summarize(form: BuiltForm): string {
  const fields = allFormFields(form);
  const labels = fields.map((f) => f.label).join(', ');
  return `Done — I built “${form.title}” with ${fields.length} fields: ${labels}. You can refine it below (e.g. “add a phone field” or “make email required”).`;
}

function cloneForm(form: BuiltForm): BuiltForm {
  return {
    title: form.title,
    description: form.description,
    coverImageUrl: form.coverImageUrl,
    inputAppearance: form.inputAppearance,
    themeColor: form.themeColor,
    pages: form.pages.map((page) => ({
      ...page,
      cta: { ...page.cta },
      fields: page.fields.map((f) => ({
        ...f,
        options: f.options ? [...f.options] : undefined,
        validation: f.validation ? { ...f.validation } : undefined,
      })),
    })),
  };
}

function refineForm(current: BuiltForm, prompt: string): GenerateResult {
  const lower = prompt.toLowerCase();
  const next = cloneForm(current);
  const page = next.pages[0] ?? createDefaultPage();
  if (!next.pages.length) {
    next.pages = [page];
  }
  const fields = page.fields;
  const added: string[] = [];

  const addIfMissing = (
    type: FieldType,
    label: string,
    extras?: Partial<FormField>,
  ) => {
    const exists = fields.some(
      (f) => f.type === type || f.label.toLowerCase() === label.toLowerCase(),
    );
    if (!exists) {
      fields.push(field(type, label, extras));
      added.push(label);
    }
  };

  if (/\b(email)\b/.test(lower) && /add|include|insert/.test(lower)) {
    addIfMissing('email', 'Email', { required: true });
  }
  if (/\b(phone|mobile)\b/.test(lower) && /add|include|insert|make/.test(lower)) {
    addIfMissing('phone', 'Phone');
  }
  if (/\b(date|deadline|start date)\b/.test(lower) && /add|include|insert/.test(lower)) {
    addIfMissing('date', 'Date');
  }
  if (
    /\b(message|comment|notes|feedback)\b/.test(lower) &&
    /add|include|insert/.test(lower)
  ) {
    addIfMissing('textarea', 'Additional notes');
  }
  if (
    /\b(checkbox|consent|agree|terms)\b/.test(lower) &&
    /add|include|insert/.test(lower)
  ) {
    addIfMissing('checkbox', 'I agree to the terms');
  }

  if (/required/.test(lower)) {
    const target =
      fields.find((f) => lower.includes(f.label.toLowerCase())) ??
      fields.find((f) => lower.includes(f.type));
    if (target) {
      target.required = true;
      return {
        form: next,
        assistantMessage: `Updated — “${target.label}” is now required.`,
      };
    }
  }

  if (/remove|delete|drop/.test(lower)) {
    const before = fields.length;
    page.fields = fields.filter((f) => !lower.includes(f.label.toLowerCase()));
    if (page.fields.length < before) {
      return {
        form: next,
        assistantMessage: `Removed the matching field(s). The form now has ${page.fields.length} fields.`,
      };
    }
  }

  if (added.length) {
    return {
      form: next,
      assistantMessage: `Updated “${next.title}” — added ${added.join(', ')}.`,
    };
  }

  const regenerated = matchTemplate(prompt);
  return {
    form: regenerated,
    assistantMessage: summarize(regenerated),
  };
}

export function generateFormFromPrompt(
  prompt: string,
  current: BuiltForm | null,
): GenerateResult {
  const trimmed = prompt.trim();
  if (!trimmed) {
    const form = defaultForm('Custom Form');
    return { form, assistantMessage: summarize(form) };
  }

  if (
    current &&
    /add|remove|delete|drop|required|make|include|insert|update|change|rename/.test(
      trimmed.toLowerCase(),
    )
  ) {
    return refineForm(current, trimmed);
  }

  const form = matchTemplate(trimmed);
  return { form, assistantMessage: summarize(form) };
}

export const FORM_SUGGESTIONS = [
  'Build a job application form',
  'Create a customer feedback survey',
  'Make a user onboarding questionnaire',
  'Design an event registration form',
] as const;
